import { useEffect, useMemo, useState } from "react";
import { api, BACKEND } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { X } from "lucide-react";
import { useI18n } from "@/i18n";

const resolveUrl = (u) => (u?.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function Galerie() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [open, setOpen] = useState(null);
  const { t, lang, alternateUrls } = useI18n();

  const TABS = [
    { id: "all", label: t("gallery.tabs.all") },
    { id: "interieur", label: t("gallery.tabs.interieur") },
    { id: "terrasse", label: t("gallery.tabs.terrasse") },
    { id: "plats", label: t("gallery.tabs.plats") },
    { id: "evenements", label: t("gallery.tabs.evenements") },
  ];

  useSEO({
    title: t("gallery.title"),
    description: lang === "en"
      ? "Browse photos of L'Entracte in Ville d'Avray: dining rooms, 240 m² flowered terrace, signature dishes and private events."
      : "Découvrez les photos de L'Entracte à Ville d'Avray : salles intérieures, terrasse fleurie de 240m², plats signature et événements privés.",
    lang,
    alternates: alternateUrls(),
  });

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data || []));
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? items : items.filter((i) => i.category === active)),
    [items, active]
  );

  return (
    <div data-testid="gallery-page" className="px-6 md:px-12 lg:px-16">
      <section className="py-20 md:py-28 max-w-[1500px] mx-auto">
        <p className="overline">{t("gallery.overline")}</p>
        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">{t("gallery.title")}</h1>
        <p className="mt-6 text-[#697585] text-base md:text-lg max-w-2xl">
          {t("gallery.sub")}
        </p>

        <div className="mt-12 flex flex-wrap gap-3" data-testid="gallery-filters">
          {TABS.map((tt) => (
            <button
              key={tt.id}
              data-testid={`gallery-filter-${tt.id}`}
              onClick={() => setActive(tt.id)}
              className={`px-5 py-2 text-xs uppercase tracking-widest border transition-colors ${active === tt.id ? "border-[#B8956A] text-[#B8956A]" : "border-[#D9D3C5] text-[#203240] hover:border-[#B8956A]/50"}`}
            >
              {tt.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto pb-28 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {filtered.map((g, idx) => (
          <button
            key={g.id}
            data-testid={`gallery-item-${g.id}`}
            onClick={() => setOpen(g)}
            className={`group overflow-hidden ${idx % 5 === 0 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-[4/5]"}`}
          >
            <img src={resolveUrl(g.url)} alt={g.alt} loading="lazy" className="w-full h-full object-cover img-soft" />
          </button>
        ))}
      </section>

      {open && (
        <div data-testid="gallery-lightbox" className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <button data-testid="lightbox-close" className="absolute top-6 right-6 text-white" onClick={() => setOpen(null)}>
            <X size={28} />
          </button>
          <img src={resolveUrl(open.url)} alt={open.alt} className="max-h-full max-w-full" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
